// Updated CampaginTable.js - Checkbox UI Fix
import React, { useEffect, useState, useRef } from "react";
import Icon from "@mui/material/Icon";
import Loader from "../../../Loader/loader";
import {
  TablePagination,
  Grid,
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  Checkbox,
} from "@mui/material";
import { FaEdit } from "react-icons/fa";
import { Tooltip } from "@mui/material";
import PropTypes from "prop-types";
import {
  getCampaignRequest,
  changeCampaignStatusRequest,
  deleteCampaignRequest,
} from "../../../api/api";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import AddCampaignForm from "../../form/AddCampaignForm";
import ScriptForm from "../../form/ScriptForm";
import MultiUpdateCampaignForm from "../../form/MultiUpdateCampaignForm";
import AddBoxIcon from "@mui/icons-material/AddBox";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import MDInput from "components/MDInput";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import EditIcon from "@mui/icons-material/Edit";
import MDButton from "components/MDButton";
import DeleteForm from "../../DeleteAlert/DeleteForm";
import DeleteIcon from "@mui/icons-material/Delete";
import CodeIcon from "@mui/icons-material/Code";

// AudioToggle component
const AudioToggle = ({ audioUrls }) => {
  const [currentAudio, setCurrentAudio] = useState(null);
  const audioRef = useRef(null);

  const handleToggle = (url) => {
    if (audioRef.current && currentAudio === url) {
      audioRef.current.pause();
      setCurrentAudio(null);
    } else {
      if (audioRef.current) audioRef.current.pause();
      const audio = new Audio(url);
      audio.crossOrigin = "anonymous";
      audio.play().catch((err) => console.error("Playback failed:", err));
      audioRef.current = audio;
      setCurrentAudio(url);
    }
  };

  return (
    <MDBox display="flex" alignItems="center" gap={1}>
      {audioUrls.filter(Boolean).map((url, idx) => (
        <Tooltip key={idx} title={`Audio ${idx + 1}`}>
          <Icon
            fontSize="small"
            sx={{ cursor: "pointer", color: currentAudio === url ? "red" : "#1A73E8" }}
            onClick={() => handleToggle(url)}
          >
            {currentAudio === url ? "pause_circle" : "play_circle"}
          </Icon>
        </Tooltip>
      ))}
    </MDBox>
  );
};

AudioToggle.propTypes = {
  audioUrls: PropTypes.arrayOf(PropTypes.string).isRequired,
};

function CampaginTable() {
  const [rows, setRows] = useState([]);
  const [campaignData, setCampaignData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [open, setOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewDtmfData, setViewDtmfData] = useState([]);
  const [dtmfModalOpen, setDtmfModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isSubmittingDelete, setIsSubmittingDelete] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openMultiUpdateDialog, setOpenMultiUpdateDialog] = useState(false);
  const [openScriptDialog, setOpenScriptDialog] = useState(false);
  const [scriptData, setScriptData] = useState(null);

  // Multi-select states
  const [selectedRows, setSelectedRows] = useState([]);

  const handleStatusToggle = async (id, currentStatus) => {
    const newStatus = currentStatus === 1 ? 0 : 1;
    try {
      await changeCampaignStatusRequest(id, {
        status: newStatus,
      });
      fetchData();
    } catch (error) {
      console.error("Failed to toggle status:", error);
    }
  };

  const handleConfirmedDelete = async () => {
    setIsSubmittingDelete(true);
    try {
      await deleteCampaignRequest(deleteId);
      fetchData();
      setIsDeleteOpen(false);
      setDeleteId(null);
    } catch (err) {
      console.error("Delete failed:", err);
    } finally {
      setIsSubmittingDelete(false);
    }
  };

  // Multi-select handlers
  const handleSelectAll = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (selectedRows.length === campaignData.length && campaignData.length > 0) {
      setSelectedRows([]);
    } else {
      const allIds = campaignData.map((item) => item.id);
      setSelectedRows([...allIds]);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedRows((prevSelected) => {
      const stringId = String(id);
      const isAlreadySelected = prevSelected.some((selectedId) => String(selectedId) === stringId);

      if (isAlreadySelected) {
        return prevSelected.filter((selectedId) => String(selectedId) !== stringId);
      } else {
        return [...prevSelected, id];
      }
    });
  };

  useEffect(() => {
    if (campaignData.length > 0) {
      setRows(generateRows(campaignData));
    }
  }, [selectedRows, campaignData]);
  const handleBulkStatusChange = async (newStatus) => {
    if (selectedRows.length === 0) {
      alert("Please select at least one campaign");
      return;
    }

    try {
      await Promise.all(selectedRows.map((id) => changeCampaignStatusRequest(id, newStatus)));
      setSelectedRows([]);
      fetchData();
      alert(`${selectedRows.length} campaigns updated successfully!`);
    } catch (error) {
      console.error("Bulk status update failed:", error);
      alert("Failed to update some campaigns");
    }
  };

  const isAllSelected = campaignData.length > 0 && selectedRows.length === campaignData.length;
  const isIndeterminate = selectedRows.length > 0 && selectedRows.length < campaignData.length;

  const columns = [
    {
      Header: (
        <MDBox display="flex" alignItems="center" justifyContent="flex-start">
          <Checkbox
            checked={isAllSelected}
            indeterminate={isIndeterminate}
            onChange={handleSelectAll}
            sx={{
              color: "#fff",
              border: "1px solid #000",
              borderRadius: "6px",
              width: "22px",
              height: "22px",
              "& .MuiSvgIcon-root": {
                fontSize: 22,
              },
              "&.Mui-checked": {
                color: "#fff",
                border: "1px solid #000",
              },
              "&.MuiCheckbox-indeterminate": {
                color: "#fff",
                border: "1px solid #000",
              },
              "&:hover": {
                border: "2px solid #000",
              },
            }}
          />
          <MDTypography
            variant="button"
            fontWeight="medium"
            color="text"
            sx={{ color: "#fff", userSelect: "none" }}
          >
            ALL
          </MDTypography>
        </MDBox>
      ),

      accessor: "select",
      align: "center",
      width: "8%",
    },

    { Header: "Campaign ID", accessor: "id", align: "left" },
    { Header: "Campaign name", accessor: "campaign_name", align: "left" },
    { Header: "Audio", accessor: "audio_file", align: "left" },
    { Header: "Description", accessor: "description", align: "center" },
    { Header: "Trunk Name", accessor: "trunk_name", align: "center" },
    { Header: "Caller Id", accessor: "caller_id", align: "center" },
    { Header: "dtmfs", accessor: "dtmfs", align: "center" },
    { Header: "Audio Play", accessor: "audio_play", align: "left" },
    { Header: "action", accessor: "action", align: "center" },
  ];

  // Function to generate rows from campaign data
  const generateRows = (data) => {
    return data.map((item) => {
      const isChecked = selectedRows.some((id) => String(id) === String(item.id));

      return {
        select: (
          <MDBox display="flex" alignItems="center" justifyContent="center" gap={1} sx={{ ml: -3 }}>
            <Checkbox
              checked={isChecked}
              onChange={() => handleSelectRow(item.id)}
              onClick={(e) => e.stopPropagation()}
              sx={{
                color: "#1A73E8",
                border: "1px solid #000", // ✅ strong black border
                borderRadius: "6px",
                width: "22px",
                height: "22px",
                "& .MuiSvgIcon-root": {
                  fontSize: 22,
                },
                "&.Mui-checked": {
                  color: "#1A73E8",
                  border: "1px solid #000", // ✅ blue border on check
                },
                "&:hover": {
                  border: "1px solid #000", // ✅ blue border on hover
                },
              }}
            />
          </MDBox>
        ),

        id: (
          <MDTypography variant="button" fontWeight="medium">
            {item.id}
          </MDTypography>
        ),
        campaign_name: (
          <MDTypography variant="button" fontWeight="medium">
            {item.campaign_name}
          </MDTypography>
        ),
        audio_file: (
          <Tooltip title={item.audio_file || "No Audio"}>
            <MDTypography variant="caption" fontWeight="medium">
              {item.audio_file
                ? item.audio_file.slice(0, 7) + (item.audio_file.length > 7 ? "..." : "")
                : ""}
            </MDTypography>
          </Tooltip>
        ),

        description: (
          <MDTypography variant="caption" fontWeight="medium">
            {item.description}
          </MDTypography>
        ),
        trunk_name: (
          <MDTypography variant="caption" fontWeight="medium">
            {item?.trunk?.name || "-"}
          </MDTypography>
        ),

        caller_id: (
          <MDTypography variant="caption" fontWeight="medium">
            {item.caller_id}
          </MDTypography>
        ),
        dtmfs: (
          <MDButton
            size="small"
            variant="outlined"
            color="info"
            onClick={() => {
              setViewDtmfData(item.dtmfs || []);
              setDtmfModalOpen(true);
            }}
          >
            View
          </MDButton>
        ),
        audio_play: (
          <AudioToggle
            audioUrls={[item.audio_file_url, item.audio_file2_url, item.audio_file3_url]}
          />
        ),

        action: (
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon
              color="warning"
              fontSize="small"
              sx={{ cursor: "pointer" }}
              onClick={() => {
                setScriptData(item);
                setOpenScriptDialog(true);
              }}
            >
              <CodeIcon />
            </Icon>

            <Icon
              color="info"
              fontSize="small"
              sx={{ cursor: "pointer" }}
              onClick={() => {
                setEditData(item);
                setOpen(true);
              }}
            >
              <EditIcon />
            </Icon>
            <Icon
              color="error"
              fontSize="small"
              sx={{ cursor: "pointer" }}
              onClick={() => {
                setDeleteId(item.id);
                setIsDeleteOpen(true);
              }}
            >
              <DeleteIcon />
            </Icon>
            <Icon
              fontSize="medium"
              sx={{ cursor: "pointer" }}
              color={item.status === 1 ? "success" : "action"}
              onClick={() => handleStatusToggle(item.id, item.status)}
            >
              {item.status === 1 ? <ToggleOnIcon /> : <ToggleOffIcon />}
            </Icon>
          </MDBox>
        ),
      };
    });
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await getCampaignRequest(page + 1, perPage, searchTerm);

      // ✅ Correct paths
      const data = res?.data?.data || [];
      const total = res?.data?.pagination?.total || 0;

      setCampaignData(data);
      setRows(generateRows(data));
      setTotalCount(total);
    } catch (error) {
      console.error("Error fetching campaigns:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Re-generate rows when selectedRows changes
  useEffect(() => {
    if (campaignData.length > 0) {
      setRows(generateRows(campaignData));
    }
  }, [selectedRows]);

  useEffect(() => {
    setSelectedRows([]);
    fetchData();
  }, [page, perPage, searchTerm]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={3}>
        <Grid item xs={12}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h6" color="white">
                {selectedRows.length > 0 && `(${selectedRows.length} selected)`}
              </MDTypography>
              <MDBox display="flex" alignItems="center" gap={2}>
                <MDInput
                  placeholder="Search here"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(0);
                  }}
                  sx={{ backgroundColor: "#ffffff", borderRadius: "10%", minWidth: "200px" }}
                  variant="outlined"
                />
                <MDButton
                  onClick={() => setOpen(true)}
                  startIcon={<AddBoxIcon />}
                  sx={{
                    backgroundColor: "#ffffff",
                    color: "#1A73E8",
                    "&:hover": { backgroundColor: "#ffffff", color: "#1A73E8" },
                  }}
                >
                  Add Campaign
                </MDButton>
                {selectedRows.length > 0 && (
                  <MDButton
                    onClick={() => {
                      setOpenMultiUpdateDialog(true);
                    }}
                    startIcon={<FaEdit />}
                    sx={{
                      backgroundColor: "#ffffff",
                      color: "#1A73E8",
                      "&:hover": { backgroundColor: "#ffffff", color: "#1A73E8" },
                    }}
                  >
                    Update Campaign
                  </MDButton>
                )}
              </MDBox>
            </MDBox>

            <MDBox pt={3}>
              {isLoading ? (
                <Grid container justifyContent="center" alignItems="center" sx={{ py: 5 }}>
                  <Loader />
                </Grid>
              ) : rows.length === 0 ? (
                <MDTypography sx={{ p: 3, textAlign: "center", color: "gray" }}>
                  No Record Found
                </MDTypography>
              ) : (
                <>
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                    loading={false}
                  />
                  <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={(event, newPage) => setPage(newPage)}
                    rowsPerPage={perPage}
                    onRowsPerPageChange={(e) => {
                      setPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                  />
                </>
              )}
            </MDBox>
          </Card>
        </Grid>
      </MDBox>

      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
          setEditData(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogContent>
          <AddCampaignForm
            onSuccess={() => {
              setOpen(false);
              setEditData(null);
              fetchData();
            }}
            initialData={editData}
            clickedBtn={editData ? "edit" : "add"}
            isSubmitting={isSubmitting}
            setSubmitting={setIsSubmitting}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={openScriptDialog}
        onClose={() => {
          setOpenScriptDialog(false);
          setScriptData(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogContent>
          <ScriptForm
            campaign={scriptData}
            onClose={() => {
              setOpenScriptDialog(false);
              setScriptData(null);
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={openMultiUpdateDialog}
        onClose={() => setOpenMultiUpdateDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogContent>
          <MultiUpdateCampaignForm
            selectedIds={selectedRows}
            onSuccess={() => {
              setOpenMultiUpdateDialog(false);
              setSelectedRows([]);
              fetchData(); // ✅ refresh table
            }}
            setSubmitting={setIsSubmitting}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={dtmfModalOpen} onClose={() => setDtmfModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>DTMF</DialogTitle>
        <DialogContent dividers>
          {viewDtmfData.length > 0 ? (
            viewDtmfData.map((opt, idx) => (
              <MDBox
                key={idx}
                mb={1.5}
                p={1.5}
                border="1px solid #ddd"
                borderRadius="8px"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="body2">
                  <strong>Input Value:</strong> {opt.input_value}
                </MDTypography>
                <MDTypography variant="body2">
                  <strong>Destination:</strong> {opt.destination}
                </MDTypography>
              </MDBox>
            ))
          ) : (
            <MDTypography variant="body2">No DTMF options available.</MDTypography>
          )}
        </DialogContent>
      </Dialog>

      <DeleteForm
        isOpen={isDeleteOpen}
        handleDelete={handleConfirmedDelete}
        onHandleClose={() => {
          setIsDeleteOpen(false);
          setDeleteId(null);
        }}
        isSubmitting={isSubmittingDelete}
      />
    </DashboardLayout>
  );
}

export default CampaginTable;
